import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../services/api';
import { joinUrl, normalizeUrl } from 'lib/utils';
import { error } from 'utils/logger';

interface JiraContextType {
  jiraConnected: boolean;
  setJiraConnected: (connected: boolean) => void;
  checkJiraAuth: () => Promise<void>;
}

const JiraContext = createContext<JiraContextType | undefined>(undefined);

export const JiraProvider = ({ children }: { children: ReactNode }) => {
  const [jiraConnected, setJiraConnected] = useState(false);
  
  const checkJiraAuth = async () => {
    const headers = getAuthHeaders();
    if (!headers) {
      error('Error checking Jira auth:', 'No authentication token found');
      return;
    }
    
    try {
      const BASE_URL = normalizeUrl(process.env.REACT_APP_BACKEND_URL || '');
      const response = await axios.get(joinUrl(BASE_URL, 'jira/check-auth'), {
        headers
      });

      if (response.status === 200 && response.data.message === 'Authenticated') {
        setJiraConnected(true);
      } else {
        setJiraConnected(false);
      }
    } catch (err) {
      setJiraConnected(false);
      error('Error checking Jira auth:', err);
    }
  };

  // Check Jira connection status when the app loads
  useEffect(() => {
    checkJiraAuth();
  }, []);

  return (
    <JiraContext.Provider value={{ jiraConnected, setJiraConnected, checkJiraAuth }}>
      {children}
    </JiraContext.Provider>
  );
};

export const useJira = () => {
  const context = useContext(JiraContext);
  if (context === undefined) {
    throw new Error('useJira must be used within a JiraProvider');
  }
  return context;
};
